import {useLocation, useNavigation} from '@github-ui/react-router'
import type {Location} from '@github-ui/react-router'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {useRouteParams} from '@github-ui/react-core/future/use-route-params'
import {useRouteQuery, useSuspenseRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {useRepositoryFilterActions} from '@github-ui/issues-shared/repository-filter-actions'
import {AnalyticsWrapper as BaseAnalyticsWrapper} from '@github-ui/repo-issues-page-layout/AnalyticsWrapper'
import {RepoIssuesPageLayout} from '@github-ui/repo-issues-page-layout/RepoIssuesPageLayout'
import {SharedDashboardBlankslate} from '@github-ui/shared-dashboard-lists/shared/SharedDashboardBlankslate'
import {useCallback, useMemo, useRef} from 'react'

import {useIssuesIndexResults} from '../hooks/use-issues-index-results'
import {IssuesListSurface} from '../components/IssuesListSurface'
import {PinnedIssuesStrip} from '../components/pinned-issues/PinnedIssuesStrip'
import {createRepoRowNodeLoader, shouldFetchReactions} from '../hooks/repo-issues-row-node'
import {useIssuesDashboardLiveUpdates} from '../hooks/use-issues-dashboard-live-updates'
import {useRowNodeOverlay} from '../hooks/use-row-node-overlay'
import {useIssuesListControls} from '../hooks/use-issues-list-controls'
import {repoIssuesToListItems} from '../helpers/repo-issues-dashboard-proto'
import {mergeDeferredIssues} from '../helpers/merge-deferred-issues'
import {issuesIndexQueryResolver, REPO_INDEX_DEFAULT_QUERY} from '../helpers/issues-index-query-resolver'
import {IssuesIndexSearch} from './IssuesIndexSearch'
import {issuesIndexContentRoute, issuesLabelContentRoute} from './issues-index-content-route'
import {issuesLayoutRoute} from './issues-layout-route'
import {useRenderSidebar} from './render-issues-sidebar'

function isSameLocation(a: Location, b: Location): boolean {
  return a.pathname === b.pathname && a.search === b.search
}

/**
 * Repo Issues#index content: a repo analog of `IssuesDashboardSurfaceContent` that
 * reuses the shared `IssuesList`, pagination, skeleton/blankslate, and a repo-scoped
 * `<Filter>` to render the mixed (issues + PRs) result list.
 */
export function IssuesIndexContent() {
  const searchInputRef = useRef<HTMLInputElement>(null)
  const navigation = useNavigation()
  const location = useLocation()
  const isNavigating = Boolean(navigation.location && !isSameLocation(navigation.location, location))
  const contentRoute = location.pathname.split('/')[3] === 'labels' ? issuesLabelContentRoute : issuesIndexContentRoute
  const {owner, repo} = useRouteParams(contentRoute)
  const {data: layoutPayload} = useRouteQuery(issuesLayoutRoute, 'mainQuery')
  const renderSidebar = useRenderSidebar(owner, repo, layoutPayload)
  const searchParams = new URLSearchParams(location.search)
  const showPinnedStrip =
    issuesIndexQueryResolver({pathname: location.pathname, searchParams}) === REPO_INDEX_DEFAULT_QUERY &&
    Number(searchParams.get('page') || 1) <= 1

  return (
    <BaseAnalyticsWrapper category="Repository Issues Index" appName="issues">
      <RepoIssuesPageLayout renderSidebar={renderSidebar} initialExpanded={layoutPayload?.sidebarExpanded}>
        <IssuesIndexSearch
          inputRef={searchInputRef}
          beforeFilter={
            showPinnedStrip ? (
              <PinnedIssuesStrip
                owner={owner}
                repo={repo}
                pinnedIssues={layoutPayload?.pinnedIssues}
                fallbackFocusRef={searchInputRef}
              />
            ) : null
          }
        />
        <ErrorBoundary
          boundaryName="IssuesIndexContent"
          fallback={
            <div className="tmp-my-3">
              <SharedDashboardBlankslate dashboardType="issues" variant="error" />
            </div>
          }
        >
          <IssuesIndexPageContent isNavigating={isNavigating} />
        </ErrorBoundary>
      </RepoIssuesPageLayout>
    </BaseAnalyticsWrapper>
  )
}

function IssuesIndexPageContent({isNavigating}: {isNavigating: boolean}) {
  const location = useLocation()
  const contentRoute = location.pathname.split('/')[3] === 'labels' ? issuesLabelContentRoute : issuesIndexContentRoute

  const {data: layoutPayload} = useRouteQuery(issuesLayoutRoute, 'mainQuery')
  const mainQuery = useSuspenseRouteQuery(contentRoute, 'mainQuery')
  const secondaryQuery = useRouteQuery(contentRoute, 'secondary')
  const data = mainQuery.data
  const {owner, repo} = useRouteParams(contentRoute)
  const {currentPage, fetchResults, fetchedQuery, getPageHref, getQueryHref} = useIssuesIndexResults()
  const scopedRepository = useMemo(() => ({owner, name: repo}), [owner, repo])
  const fetchReactions = shouldFetchReactions(fetchedQuery)
  const rowNodeLoader = useMemo(
    () => createRepoRowNodeLoader(owner, repo, fetchReactions),
    [owner, repo, fetchReactions],
  )
  const rowNodeQueryScope = `${owner}/${repo}:fetch-reactions=${fetchReactions}`

  const applySectionFilter = useCallback(
    (query: string) => {
      fetchResults({q: query})
    },
    [fetchResults],
  )
  const filterActions = useRepositoryFilterActions({
    repo: scopedRepository,
    query: fetchedQuery,
    applySectionFilter,
    issueTypesEnabled: layoutPayload?.issueTypesEnabled ?? false,
  })

  const secondaryResults = secondaryQuery.data?.payload?.issuesIndexSecondaryData.results
  const listRows = useMemo(
    () => mergeDeferredIssues(repoIssuesToListItems(data.results ?? []), secondaryResults ?? []),
    [data.results, secondaryResults],
  )
  // Per-row live-update overlay: an Alive event refreshes just that row via the
  // repo `_rows` endpoint instead of refetching the list.
  const issues = useRowNodeOverlay({
    rows: listRows,
    listUpdatedAt: mainQuery.dataUpdatedAt,
    loadRowNode: rowNodeLoader.loadRowNode,
    queryKeyScope: rowNodeQueryScope,
  })
  const liveUpdates = useIssuesDashboardLiveUpdates(listRows)

  const defaultSortingKey = layoutPayload?.bestMatchSortEnabled ? 'relevance' : 'created'
  const pageInfo = data.pageInfo ?? {currentPage: 1, totalPages: 1, totalCount: 0}
  // Persisted density + screen-reader announcements, shared with the dashboard. Density is
  // server-seeded from the layout payload so the list renders at the persisted density on first paint.
  const {compactMode, onCompactModeChange, emptyListState} = useIssuesListControls({
    initialCompactMode: layoutPayload?.compactMode ?? false,
    compactModePreferenceKey: 'dashboard_surface_compact_mode',
    isFetching: isNavigating,
    currentPage,
    fetchedQuery,
    defaultSortingKey,
    totalCount: pageInfo.totalCount,
    totalPages: pageInfo.totalPages,
    errorMessage: data.error || undefined,
  })

  const error = data.error || null

  if (error) {
    return (
      <div className="tmp-my-3">
        <SharedDashboardBlankslate dashboardType="issues" variant="error" />
      </div>
    )
  }

  return (
    <IssuesListSurface
      response={data}
      routeAdapter={{
        scopeIdentity: `repository:${owner}/${repo}`,
        query: fetchedQuery,
        page: currentPage,
        fetch: ({query, page}) => fetchResults({q: query, page}),
        getPageHref,
        getQueryHref,
      }}
      filterActions={filterActions}
      defaultSortingKey={defaultSortingKey}
      isLoading={isNavigating}
      issuesOverride={issues}
      beforeList={liveUpdates}
      compactMode={compactMode}
      onCompactModeChange={onCompactModeChange}
      emptyListStateOverride={emptyListState}
    />
  )
}
